import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "lumiere_token";

// "Se souvenir de moi" persiste le token en localStorage (survit à la
// fermeture du navigateur, jusqu'à l'expiration serveur du token) ; sans
// cette option, il vit en sessionStorage (effacé à la fermeture de
// l'onglet/navigateur) et le serveur lui donne de toute façon une durée
// de vie beaucoup plus courte. Les deux emplacements ne sont jamais
// utilisés en même temps pour éviter tout token orphelin obsolète.
export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(token, remember) {
  if (remember) {
    localStorage.setItem(TOKEN_KEY, token);
    sessionStorage.removeItem(TOKEN_KEY);
  } else {
    sessionStorage.setItem(TOKEN_KEY, token);
    localStorage.removeItem(TOKEN_KEY);
  }
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
}

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Identifiant d'appareil (session de tracking) : permet au backend de bloquer un
  // appareil même si son adresse IP change (mobile). track.js le renseigne aussi
  // pour les sessions éphémères (sans consentement cookies).
  if (!config.headers["X-Session-Id"]) {
    try {
      const sid = localStorage.getItem("lumiere_sid") || api.defaults.headers.common["X-Session-Id"];
      if (sid) config.headers["X-Session-Id"] = sid;
    } catch {
      /* stockage indisponible */
    }
  }
  return config;
});

// Intercepteur de réponse : si le serveur renvoie ACCOUNT_BLOCKED sur un
// appel authentifié (token déjà présent), on vide le token et on redirige
// vers /compte-bloque. Évite qu'un utilisateur bloqué continue à naviguer
// avec un token encore valide côté client.
// Blocage géographique / IP (middleware backend) : le HTML de l'application
// est servi indépendamment de l'API, donc sans cette redirection un visiteur
// bloqué verrait simplement un site vide au lieu d'un message clair.
export const ACCESS_BLOCKED_PATH = "/acces-refuse";
// Boutique suspendue (abonnement) : l'API répond SHOP_SUSPENDED aux visiteurs et
// clients. Seule la vitrine est fermée : la connexion et le back-office restent ouverts.
export const SHOP_SUSPENDED_PATH = "/boutique-suspendue";
const SUSPENSION_EXEMPT_PREFIXES = [
  SHOP_SUSPENDED_PATH, "/admin", "/connexion", "/mot-de-passe-oublie", "/reinitialiser-mot-de-passe",
];

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const data = err.response?.data;
    const detail = data?.detail;
    const code = typeof detail === "object" ? detail?.code : data?.code;
    if (code === "SHOP_SUSPENDED") {
      const path = window.location.pathname;
      if (!SUSPENSION_EXEMPT_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) {
        window.location.href = SHOP_SUSPENDED_PATH;
      }
    } else if (code === "ACCOUNT_BLOCKED" && getToken()) {
      clearToken();
      window.location.href = "/compte-bloque";
    } else if (
      err.response?.status === 403 &&
      (code === "GEO_BLOCKED" || code === "IP_BLOCKED" || code === "SESSION_BLOCKED" || code === "AUTO_BLOCKED") &&
      window.location.pathname !== ACCESS_BLOCKED_PATH
    ) {
      const reason = code === "IP_BLOCKED" || code === "AUTO_BLOCKED" ? "ip" : code === "SESSION_BLOCKED" ? "session" : "geo";
      window.location.href = `${ACCESS_BLOCKED_PATH}?reason=${reason}`;
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const eur = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n);

export default api;
